/**
 * Pure helpers that turn an email thread's timeline events into an ordered,
 * de-duplicated list of conversation messages.
 *
 * The thread entity itself stores no messages — each email is an
 * `ahoy_email_sent` / `ahoy_email_received` event on the thread (see
 * `apps/crm_events/schemas/event_types.yaml`). The view-state API returns
 * events newest-first; a readable conversation is oldest-first with the
 * sent-copy/received-copy of one message collapsed to a single row.
 *
 * Kept free of React so the ordering/dedup/labeling rules are unit-testable
 * in isolation and shared between the conversation section and the generic
 * timeline's email rows.
 */

import {
  extractDisplayName,
  extractEmail,
} from '@/lib/crm/utils/email-address';

import { collapseTimelineEvents } from './collapseTimelineEvents';
import type {
  EmailAttachmentMeta,
  EmailPayload,
  EmailReceivedEvent,
  EmailSentEvent,
  TimelineEvent,
} from './types';
import { EVENT_TYPES } from './types';

/**
 * The timeline filters every email-thread surface queries with. One shared
 * constant — the conversation, the participants hook, and the header's reply
 * action all pass the SAME reference so their react-query keys align and one
 * fetch serves all three. `includeAssociated` MUST be explicitly false: it
 * excludes associated entities' events, which would surface wrong messages
 * and participant addresses (see the registry's timelineConfig comment).
 */
export const THREAD_TIMELINE_FILTERS = { includeAssociated: false };

/** One renderable message within an email thread conversation. */
export interface ThreadMessage {
  /** Stable id for React keys and expand/collapse state. */
  eventId: string;
  /** The thread entity this message belongs to. */
  entityId: string;
  /** True for `ahoy_email_sent` (a message the mailbox owner wrote). */
  isOutbound: boolean;
  /** ISO timestamp the message occurred at. */
  occurredAt: string;
  /** The email payload (subject, addresses, bodies). */
  payload: EmailPayload;
  /** The underlying event, for consumers that need the full record. */
  event: EmailSentEvent | EmailReceivedEvent;
}

function isEmailMessageEvent(
  event: TimelineEvent
): event is EmailSentEvent | EmailReceivedEvent {
  return (
    event.event_type_name === EVENT_TYPES.EMAIL_SENT ||
    event.event_type_name === EVENT_TYPES.EMAIL_RECEIVED
  );
}

/**
 * Build the conversation from raw timeline events.
 *
 * @param events Events as returned by the view-state API (newest-first —
 *   `collapseTimelineEvents` relies on that adjacency for its dedup windows).
 * @returns Messages oldest-first, one per logical email.
 */
export function buildThreadMessages(events: TimelineEvent[]): ThreadMessage[] {
  const collapsed = collapseTimelineEvents(events);
  const messages: ThreadMessage[] = [];
  for (const event of collapsed) {
    if (!isEmailMessageEvent(event)) {
      continue;
    }
    messages.push({
      eventId: event.event_id,
      entityId: event.entity_id,
      isOutbound: event.event_type_name === EVENT_TYPES.EMAIL_SENT,
      occurredAt: event.occurred_at,
      payload: event.payload ?? {},
      event,
    });
  }
  // View-state order is newest-first; a conversation reads oldest-first.
  messages.reverse();
  return messages;
}

/**
 * Human label for the message's author, derived from the email envelope.
 *
 * - The current user's own address → `'You'`
 * - Address resolves to a CRM entity (via `resolveEmailName`) → its name
 * - `Jane Smith <j@acme.com>` → `Jane Smith`
 * - Name-shaped local part → humanized (`jennifer.walsh@…` → `Jennifer Walsh`)
 * - Otherwise → the bare address (recognizable, never invented)
 * - No `from` at all → `undefined` (caller supplies its fallback)
 */
export function senderLabelForPayload(
  payload: EmailPayload | undefined,
  currentUserEmail: string | undefined | null,
  resolveEmailName?: (email: string) => string | undefined
): string | undefined {
  const raw = payload?.from;
  if (!raw || !raw.trim()) {
    return undefined;
  }
  const email = extractEmail(raw);
  if (email && currentUserEmail && email === currentUserEmail.toLowerCase()) {
    return 'You';
  }
  return emailAddressLabel(raw, resolveEmailName);
}

/**
 * Human label for a raw address header (`Name <email>` or bare address),
 * shared by sender lines and recipient summaries. Resolution order:
 * CRM entity name (canonical — matches badges elsewhere) → header display
 * name → humanized name-shaped local part → the bare address.
 */
export function emailAddressLabel(
  raw: string,
  resolveEmailName?: (email: string) => string | undefined
): string {
  const email = extractEmail(raw);
  if (email && resolveEmailName) {
    const resolved = resolveEmailName(email);
    if (resolved?.trim()) {
      return resolved.trim();
    }
  }
  const headerName = extractDisplayName(raw);
  if (headerName) {
    return headerName;
  }
  if (email) {
    return humanizeEmailLocalPart(email) ?? email;
  }
  return raw.trim();
}

/**
 * Role-account local parts that must never read as a person's name
 * ("No Reply", "Mailer Daemon"). Kept to the classic offenders — a role
 * address rendering as its raw form is fine; a robot rendering as a person
 * is not.
 */
const NON_PERSON_LOCAL_PARTS = new Set([
  'no-reply',
  'noreply',
  'no_reply',
  'do-not-reply',
  'donotreply',
  'mailer-daemon',
  'postmaster',
]);

/**
 * Turn a name-shaped email local part into a display name:
 * `jennifer.walsh` → `Jennifer Walsh`, `sarah_chen` → `Sarah Chen`.
 *
 * Deliberately conservative: only 2–3 purely-alphabetic segments qualify, so
 * `support@`, `d.a.v.i.d.x@`, and `john2@` all fall through to the raw
 * address rather than gaining an invented identity.
 */
export function humanizeEmailLocalPart(email: string): string | undefined {
  const local = email.split('@', 1)[0]?.toLowerCase();
  if (!local || NON_PERSON_LOCAL_PARTS.has(local)) {
    return undefined;
  }
  const segments = local.split(/[._-]/).filter(Boolean);
  if (segments.length < 2 || segments.length > 3) {
    return undefined;
  }
  if (!segments.every(segment => /^[a-z]+$/.test(segment))) {
    return undefined;
  }
  return segments
    .map(segment => segment[0].toUpperCase() + segment.slice(1))
    .join(' ');
}

/**
 * Avatar initials for the VIEWER — "You" is the display label for their own
 * messages/pills, but the avatar should still carry their identity ("JD"),
 * not a literal "Y". Prefers the account's display name; falls back to a
 * name-shaped email local part, then the address itself. Shared by the
 * conversation's sender avatars and the header's participant stack so the
 * viewer wears ONE face across the thread surface.
 */
export function viewerAvatarInitials(
  display: string | undefined | null,
  email: string | undefined | null
): string | undefined {
  const normalizedEmail = email?.toLowerCase();
  const trimmedDisplay = display?.trim();
  if (trimmedDisplay && trimmedDisplay.toLowerCase() !== normalizedEmail) {
    return initialsForLabel(trimmedDisplay);
  }
  if (!normalizedEmail) {
    return undefined;
  }
  return initialsForLabel(
    humanizeEmailLocalPart(normalizedEmail) ?? normalizedEmail
  );
}

/**
 * Up-to-two-letter initials for an avatar from a sender label.
 * `'You'` and addresses fall back to their first letter.
 */
export function initialsForLabel(label: string): string {
  const cleaned = label.trim();
  if (!cleaned) {
    return '?';
  }
  if (cleaned.includes('@')) {
    return cleaned[0].toUpperCase();
  }
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}

/**
 * The plain-text body to show when no renderable HTML is present. Mirrors the
 * timeline's whitespace-only handling: HTML-only emails ship `body_text` as a
 * single space, which must read as absent.
 */
export function plainTextBodyForPayload(
  payload: EmailPayload | undefined
): string | undefined {
  const bodyText = payload?.body_text;
  if (bodyText?.trim()) {
    return bodyText;
  }
  const legacyBody = payload?.body;
  if (legacyBody?.trim()) {
    return legacyBody;
  }
  return undefined;
}

/** The renderable HTML body, treating whitespace-only as absent. */
export function htmlBodyForPayload(
  payload: EmailPayload | undefined
): string | undefined {
  const bodyHtml = payload?.body_html;
  return bodyHtml?.trim() ? bodyHtml : undefined;
}

/**
 * The renderable attachment entries for a message. Filters out entries with no
 * usable display name — a malformed ingest entry would otherwise render an
 * unlabeled chip. Order is preserved from the payload (provider part order).
 */
export function attachmentsForPayload(
  payload: EmailPayload | undefined
): EmailAttachmentMeta[] {
  const attachments = payload?.attachments;
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return [];
  }
  return attachments.filter(entry => entry?.name?.trim());
}

/** One-line preview for a collapsed message row. */
export function snippetForPayload(
  payload: EmailPayload | undefined
): string | undefined {
  // Pick the first NON-whitespace value: a provider may send a whitespace-only
  // `snippet` alongside a real `body_preview`. A plain `snippet || body_preview`
  // would select the truthy whitespace string, and the trim() below would then
  // clear it — blanking the row instead of falling back to body_preview.
  const snippet = payload?.snippet?.trim() || payload?.body_preview;
  if (snippet?.trim()) {
    return snippet.trim();
  }
  const plain = plainTextBodyForPayload(payload);
  if (!plain) {
    return undefined;
  }
  const firstLine = plain.trim().split('\n', 1)[0];
  return firstLine.length > 140 ? `${firstLine.slice(0, 140)}…` : firstLine;
}

/**
 * Compact humanized recipient summary for a message header — mail-client
 * style: `Jennifer Walsh, Sarah Chen`, `you, Jennifer Walsh`,
 * `Jennifer Walsh · cc Sarah Chen`, capping each list at two names
 * (`Jennifer Walsh, Sarah Chen +2`). Returns null with no recipients.
 */
export function recipientsSummaryForPayload(
  payload: EmailPayload | undefined,
  currentUserEmail: string | undefined | null,
  resolveEmailName?: (email: string) => string | undefined
): string | null {
  const labelFor = (raw: string): string => {
    const email = extractEmail(raw);
    if (email && currentUserEmail && email === currentUserEmail.toLowerCase()) {
      // Deliberately lowercase, unlike `senderLabelForPayload`'s 'You':
      // the sender label stands alone in name position ("You"), while this
      // summary reads as prose inside the "to …" line ("to you · cc Jane") —
      // sentence-position casing, matching how mail clients write it.
      return 'you';
    }
    return emailAddressLabel(raw, resolveEmailName);
  };
  const summarize = (addresses: string[] | undefined): string | null => {
    const labels = (addresses ?? []).map(labelFor).filter(Boolean);
    if (labels.length === 0) {
      return null;
    }
    const shown = labels.slice(0, 2).join(', ');
    return labels.length > 2 ? `${shown} +${labels.length - 2}` : shown;
  };

  const toPart = summarize(payload?.to);
  const ccPart = summarize(payload?.cc);
  if (!toPart && !ccPart) {
    return null;
  }
  const parts: string[] = [];
  if (toPart) {
    parts.push(toPart);
  }
  if (ccPart) {
    parts.push(`cc ${ccPart}`);
  }
  return parts.join(' · ');
}

/**
 * Canonical comparison key for an email subject: strips any run of
 * `Re:` / `Fw:` / `Fwd:` prefixes, collapses whitespace, lowercases.
 * Two messages whose keys match are "the same subject" — used to detect
 * mid-thread subject changes worth surfacing (mail clients show the new
 * subject inline when a thread drifts). Empty/absent subjects key to ''.
 */
export function normalizeSubjectKey(subject: string | undefined): string {
  if (!subject) {
    return '';
  }
  let s = subject.trim();
  // Peel repeated reply/forward prefixes: "Re: RE: Fwd: X" → "X".
  for (;;) {
    const next = s.replace(/^(re|fw|fwd)\s*:\s*/i, '');
    if (next === s) {
      break;
    }
    s = next;
  }
  return s.replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Timestamp for a floating card header — always absolute (`MMM d, HH:mm` /
 * `MMM d, h:mm AM`). Unlike `dateGroupLabel`, never uses Today/Yesterday:
 * the card stands alone outside a day-divider context, so a relative label
 * would be confusing. Year is added when the message is from a prior year.
 */
export function cardTimestampLabel(
  iso: string,
  hour12: boolean,
  now: Date = new Date()
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: hour12 ? 'numeric' : '2-digit',
    minute: '2-digit',
    hour12,
  };
  if (date.getFullYear() !== now.getFullYear()) {
    options.year = 'numeric';
  }
  return new Intl.DateTimeFormat(undefined, options).format(date);
}

/**
 * Date label for a date-group header — always absolute (`MMM d`). Never uses
 * Today/Yesterday: the group header in the floating-card layout gives the
 * calendar date, not a relative label. Year is added when the message is from
 * a prior year.
 */
export function dateGroupLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const options: Intl.DateTimeFormatOptions =
    date.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };
  return new Intl.DateTimeFormat(undefined, options).format(date);
}
